import { formatUnits, getAddress, type Hex, isAddress, isHex, maxUint256 } from 'viem'
import { z } from 'zod'

import { safeParseUnits } from './safeParseUnits'

export const AMOUNT_REQUIRED_MESSAGE = 'Enter an amount'

export const bigintStringSchema = z.string().regex(/^\d+$/, 'Must be a non-negative numeric string')

/**
 * A uint256 carried as a decimal string (raw token units, shares). zod runs the refine even
 * when an earlier check failed, so it only calls `BigInt()` on 1 to 78 digits.
 */
export const uint256StringSchema = z
  .string()
  .regex(/^\d{1,78}$/, 'Invalid uint256 string')
  .refine(value => !/^\d{1,78}$/.test(value) || BigInt(value) <= maxUint256, 'Above uint256')

export const hexSchema = z
  .string()
  .refine(isHex, 'Invalid hex format')
  .transform(v => v as Hex)

export const marketIdSchema = z.string().length(66, 'Invalid market ID').pipe(hexSchema)

export const addressSchema = z
  .string()
  .trim()
  .min(1, 'Missing address')
  .refine(value => isAddress(value, { strict: false }), 'Invalid address')
  .transform(value => getAddress(value))

export function createOnchainAmountSchema({
  decimals,
  min = 1n, // 1n is equivalent to positive (0n is not valid)
  minErrorMessage = min === 1n
    ? AMOUNT_REQUIRED_MESSAGE
    : `Must be greater than ${formatUnits(min, decimals)}.`,
  max = maxUint256,
  maxErrorMessage = `Must be less than ${formatUnits(max, decimals)}.`
}: {
  decimals: number
  min?: bigint
  minErrorMessage?: string
  max?: bigint
  maxErrorMessage?: string
}) {
  return z
    .string()
    .transform((value, ctx) => {
      const parsed = safeParseUnits(value, decimals)

      if (parsed === null) {
        ctx.addIssue({
          code: 'custom',
          message: 'Invalid number'
        })
        return z.NEVER
      }

      return parsed
    })
    .pipe(z.bigint().min(min, { message: minErrorMessage }).max(max, { message: maxErrorMessage }))
}
